import { Button, ConfigProvider, Image, Tooltip } from "antd";
import { CSSProperties, FC } from "react";
import { COLOR_VALUES } from "../../../lib/types/shared/enums";
import { GButtonProps } from "../../../lib/types/shared/reactInherited";
import { antdGlobalTheme } from "../../../themes/constants";

/**
 * @since August 2023
 * @author Issa Jean Marie <jeanmarieissa@gmail.com>
 * @see {@link https://issadevs.com} - Author's website
 * @returns {JSX.Element}
 */

const G_Button: FC<GButtonProps> = ({
  faded = false,
  extraFaded = false,
  type = "primary",
  isDynamic = false,
  bg,
  form,
  color,
  uppercase = true,
  children,
  iconSrc,
  category,
  size = category === "iconic" ? "middle" : "large",
  tooltipTitle,
  showTooltip = false,
  isShort = false,
  toolTipPlacement,
  iconWidth = 14,
  iconHeight = 14,
  rounded = false,
  borderColor,
  minWidth,
  style,
  ...rest
}): JSX.Element => {
  const isIconic = category === "iconic";

  const styles: CSSProperties = {
    fontSize: "11px",
    height: isShort ? "30px" : "34px",
    borderRadius: rounded ? "100px" : "",
    border: borderColor ? `1px solid ${COLOR_VALUES[borderColor]}` : "",
    minWidth:
      minWidth ||
      (isDynamic || category == "iconic" ? "" : isShort ? "34px" : "70px"),
    background:
      faded && !extraFaded && !bg
        ? COLOR_VALUES["ox_2_faded_primary"]
        : extraFaded && !bg
        ? COLOR_VALUES["ox_2_extra_faded_primary"]
        : bg
        ? COLOR_VALUES[bg]
        : type === "primary"
        ? COLOR_VALUES["ox_2_primary"]
        : COLOR_VALUES["ox_2_transparent"],
    color: color ? COLOR_VALUES[color] : COLOR_VALUES["ox_2_black"],
    padding: !isDynamic && !isIconic ? "0 12px" : ""
  };

  return (
    <ConfigProvider theme={antdGlobalTheme}>
      <Tooltip
        title={tooltipTitle || (showTooltip && children)}
        placement={toolTipPlacement}
      >
        <Button
          icon={
            iconSrc && (
              <Image
                src={iconSrc}
                width={iconWidth}
                height={iconHeight}
                alt=""
                preview={false}
                className="m-auto"
              />
            )
          }
          form={form}
          type={type || "primary"}
          style={{ ...styles, ...style }}
          size={size}
          className={`${uppercase ? "uppercase" : ""} font-semibold ${
            iconSrc ? "flex items-center justify-center gap-0" : ""
          }`}
          {...rest}
        >
          {children}
        </Button>
      </Tooltip>
    </ConfigProvider>
  );
};

export default G_Button;
